import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Plus, ChevronRight, LogOut, Crown, Briefcase, Wallet, Pencil, Check, X, Trash2,
  Users, UserPlus, Shield, Clock, Mail, Monitor, Calculator, ExternalLink, Activity, Settings as SettingsIcon, Search,
} from 'lucide-react';
import { currencySymbol, fmtNum } from '../utils/finance';
import useTabAccess from '../hooks/useTabAccess';
import NeedsAttention from '../components/NeedsAttention';
import WelcomeModal from '../components/WelcomeModal';
import AlertBell from '../components/AlertBell';
import FamilyOfficeSwitcher from '../components/FamilyOfficeSwitcher';
import SeoRollupTab from '../components/SeoRollupTab';
import { useFamilyOffice } from '../contexts/FamilyOfficeContext';

const ALL_ROLES = [
  { value: 'owner', label: 'Owner (full access)' },
  { value: 'viewer', label: 'Viewer (read-only)' },
  { value: 'ops_staff', label: 'Ops Staff (Internal OS)' },
  { value: 'content_manager', label: 'Content Manager (Internal OS)' },
  { value: 'biz_dev', label: 'Business Development (Internal OS)' },
  { value: 'trainer', label: 'Trainer (Internal OS - limited)' },
  { value: 'accountant', label: 'Accountant (Recon + Bookkeeper)' },
];

export default function BusinessList({ user, businesses, logout, createBusiness, updateBusiness, deleteBusiness, inviteToBusiness, getPendingInvites, revokeInvite, revokeAccess, pettyCashBalance, pettyCashCurrency, alerts = [], alertsLoading = false }) {
  const { tabAccess } = useTabAccess(user);
  const { activeFo, activeFoId, accessibleFos } = useFamilyOffice();
  const [activeTab, setActiveTab] = useState('businesses');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [newOwnership, setNewOwnership] = useState('100');
  const [newIsProject, setNewIsProject] = useState(false);
  const [newCurrency, setNewCurrency] = useState('SGD');

  const sortByOrder = (a, b) => (a.order ?? 9999) - (b.order ?? 9999);

  // Check if user is owner of any business
  const isAnyOwner = businesses.some((b) => b.roles[user.uid] === 'owner');

  // Filter to active FO (if set). Untagged businesses show only when no FO is active.
  const inActiveFo = (b) => !activeFoId || b.family_office_id === activeFoId;
  const notArchived = (b) => !b.archived;

  const myBusinesses = businesses
    .filter((b) => b.roles[user.uid] === 'owner' && !b.isProject && inActiveFo(b) && notArchived(b))
    .sort(sortByOrder);
  const projects = businesses
    .filter((b) => b.roles[user.uid] === 'owner' && b.isProject && inActiveFo(b) && notArchived(b))
    .sort(sortByOrder);

  // Businesses where user has a non-owner role (staff view)
  const assignedBusinesses = businesses
    .filter((b) => b.roles[user.uid] && b.roles[user.uid] !== 'owner' && inActiveFo(b) && notArchived(b))
    .sort(sortByOrder);

  const [draggedId, setDraggedId] = useState(null);

  const handleDragStart = (id) => setDraggedId(id);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (targetId, list) => {
    if (!draggedId || draggedId === targetId) return;
    const draggedIdx = list.findIndex((b) => b.id === draggedId);
    const targetIdx = list.findIndex((b) => b.id === targetId);
    if (draggedIdx === -1 || targetIdx === -1) return;

    const reordered = [...list];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    // Persist new order
    for (let i = 0; i < reordered.length; i++) {
      if (reordered[i].order !== i) {
        await updateBusiness(reordered[i].id, { order: i });
      }
    }
    setDraggedId(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    await createBusiness({
      name: newName.trim(),
      type: newType.trim() || 'Business',
      ownershipPct: newIsProject ? 0 : (parseInt(newOwnership) || 100),
      isProject: newIsProject,
      currencies: [newCurrency],
      family_office_id: activeFoId || 'NK',
    });
    setNewName('');
    setNewType('');
    setNewOwnership('100');
    setNewIsProject(false);
    setNewCurrency('SGD');
    setShowCreate(false);
  };

  return (
    <div className="page-container">
      <WelcomeModal />
      <header className="page-header">
        <div className="header-left">
          <h1 className="app-title">UniHub</h1>
        </div>
        <div className="header-right">
          <FamilyOfficeSwitcher />
          {isAnyOwner && (
            <>
              <Link to="/petty-cash" className="header-pc-badge">
                <Wallet size={14} />
                <span className={`header-pc-amount mono ${pettyCashBalance >= 0 ? '' : 'negative'}`}>
                  {currencySymbol(pettyCashCurrency || 'SGD')}{fmtNum(pettyCashBalance ?? 0)}
                </span>
              </Link>
              <span className="header-divider" />
            </>
          )}
          <span className="user-email">{user.email}</span>
          <AlertBell alerts={alerts} loading={alertsLoading} />
          <Link to="/settings" className="btn btn-ghost" title="Settings">
            <SettingsIcon size={16} />
          </Link>
        </div>
      </header>

      {/* Portfolio tabs - only for owners */}
      {isAnyOwner && (
        <nav className="portfolio-tabs">
          {tabAccess.business !== false && (
            <button className={`portfolio-tab ${activeTab === 'businesses' ? 'active' : ''}`} onClick={() => setActiveTab('businesses')}>
              <Crown size={16} /> <span>{activeFo?.module_names?.business || 'Businesses'}</span>
            </button>
          )}
          {tabAccess.projects !== false && (
            <button className={`portfolio-tab ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => setActiveTab('projects')}>
              <Briefcase size={16} /> <span>Projects</span>
            </button>
          )}
          <button className={`portfolio-tab ${activeTab === 'seo' ? 'active' : ''}`} onClick={() => setActiveTab('seo')}>
            <Search size={16} /> <span>SEO</span>
          </button>
          <button
            className="portfolio-tab portfolio-tab-office"
            onClick={() => {
              const baseUrl = process.env.REACT_APP_NK_OFFICE_URL || 'http://localhost:3000';
              const url = activeFo ? `${baseUrl}/?fo=${encodeURIComponent(activeFo.short_code || activeFo.id)}` : baseUrl;
              window.open(url, '_blank');
            }}
            title={`Open Office${activeFo ? ' (' + (activeFo.short_code || activeFo.name) + ')' : ''} in new tab`}
          >
            <ExternalLink size={16} /> <span>{activeFo?.module_names?.office || 'Office'}</span>
          </button>
          <button
            className="portfolio-tab portfolio-tab-folio"
            onClick={async () => {
              const baseUrl = process.env.REACT_APP_FOLIO_URL || 'https://folio.nexkoins.com';
              const fo = activeFo?.short_code || activeFo?.id || 'NK';
              try {
                const idToken = await user.getIdToken();
                window.open(`${baseUrl}/auth/from-unihub?fo=${encodeURIComponent(fo)}#token=${idToken}`, '_blank');
              } catch (e) {
                window.open(baseUrl, '_blank');
              }
            }}
            title={`Open Folio${activeFo ? ' (' + (activeFo.short_code || activeFo.name) + ')' : ''} in new tab`}
          >
            <ExternalLink size={16} /> <span>Folio</span>
          </button>
        </nav>
      )}

      <main className="business-list-main">
        {/* Tab header */}
        {/* ── BUSINESSES TAB ── */}
        {activeTab === 'businesses' && (
          <>
            {isAnyOwner && (
              <NeedsAttention user={user} businesses={businesses.filter(inActiveFo)} getPendingInvites={getPendingInvites} />
            )}
            <div className="section-header">
              <h2>My Businesses{activeFo ? ` — ${activeFo.short_code || activeFo.name}` : ''}</h2>
              {isAnyOwner && (
                <button className="btn btn-primary" onClick={() => { setShowCreate(true); setNewIsProject(false); }}>
                  <Plus size={18} /> New Business
                </button>
              )}
            </div>

            {showCreate && !newIsProject && (
              <form className="create-business-form" onSubmit={handleCreate}>
                <input type="text" placeholder="Business name" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
                <input type="text" placeholder="Type (e.g. Gym, SaaS, Consulting)" value={newType} onChange={(e) => setNewType(e.target.value)} />
                <div className="ownership-input-row">
                  <label>Ownership %</label>
                  <input type="number" min="1" max="100" value={newOwnership} onChange={(e) => setNewOwnership(e.target.value)} className="ownership-input" />
                  <span className="unit">%</span>
                </div>
                <div className="ownership-input-row">
                  <label>Primary currency</label>
                  <select value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)} className="currency-select">
                    {['SGD', 'USD', 'JPY', 'EUR', 'GBP', 'THB', 'IDR', 'MVR'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Create</button>
                  <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </form>
            )}

            <div className="business-cards">
              {myBusinesses.map((b) => (
                <BusinessCard key={b.id} business={b} showOwnership onUpdate={updateBusiness} onDelete={deleteBusiness}
                  onDragStart={() => handleDragStart(b.id)} onDragOver={handleDragOver} onDrop={() => handleDrop(b.id, myBusinesses)} isDragging={draggedId === b.id} />
              ))}
            </div>

            {/* Assigned businesses (for non-owner staff) */}
            {assignedBusinesses.length > 0 && (
              <div className="business-group" style={{ marginTop: 24 }}>
                <h3 className="group-label"><Building2 size={16} /> Assigned to me</h3>
                <div className="business-cards">
                  {assignedBusinesses.map((b) => (
                    <Link key={b.id} to={`/business/${b.id}`} className="business-card">
                      <div className="business-card-content">
                        <Building2 size={24} className="business-icon" />
                        <div>
                          <h4 className="business-name">
                            {b.name}{' '}
                            {b.ledgerEntityId
                              ? <span style={{ color: '#6b6b6b', fontWeight: 400 }}>({b.ledgerEntityId})</span>
                              : <span style={{ color: '#9b9b97', fontWeight: 400, fontStyle: 'italic' }}>(no id)</span>}
                          </h4>
                          <div className="business-meta">
                            <span className="business-type">{b.type}</span>
                            <span className="ownership-badge">{b.roles[user.uid]}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={20} className="chevron" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {myBusinesses.length === 0 && assignedBusinesses.length === 0 && (
              <div className="empty-state"><Building2 size={48} /><p>No businesses yet.</p></div>
            )}
          </>
        )}

        {/* ── PROJECTS TAB ── */}
        {activeTab === 'projects' && (
          <>
            <div className="section-header">
              <h2>Projects</h2>
              {isAnyOwner && (
                <button className="btn btn-primary" onClick={() => { setShowCreate(true); setNewIsProject(true); }}>
                  <Plus size={18} /> New Project
                </button>
              )}
            </div>

            {showCreate && newIsProject && (
              <form className="create-business-form" onSubmit={handleCreate}>
                <input type="text" placeholder="Project name" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
                <input type="text" placeholder="Type (e.g. Ski Resort, Chartering)" value={newType} onChange={(e) => setNewType(e.target.value)} />
                <div className="ownership-input-row">
                  <label>Primary currency</label>
                  <select value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)} className="currency-select">
                    {['SGD', 'USD', 'JPY', 'EUR', 'GBP', 'THB', 'IDR', 'MVR'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Create</button>
                  <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </form>
            )}

            <div className="business-cards">
              {projects.map((b) => (
                <BusinessCard key={b.id} business={b} onUpdate={updateBusiness} onDelete={deleteBusiness}
                  onDragStart={() => handleDragStart(b.id)} onDragOver={handleDragOver} onDrop={() => handleDrop(b.id, projects)} isDragging={draggedId === b.id} />
              ))}
            </div>

            {projects.length === 0 && (
              <div className="empty-state"><Briefcase size={48} /><p>No projects yet.</p></div>
            )}
          </>
        )}

        {activeTab === 'seo' && (
          <SeoRollupTab businesses={[...myBusinesses, ...projects]} />
        )}

        {/* Portfolio + Treasury tabs removed — now owned by NK Office.
            See OfficeTile above Businesses. */}

        {/* Access management moved to /access page (top-right nav) */}

        {/* Non-owner staff see their workspaces without tabs */}
        {!isAnyOwner && (
          <>
            <div className="section-header"><h2>My Workspaces</h2></div>
            <div className="business-cards">
              {assignedBusinesses.map((b) => (
                <Link key={b.id} to={`/business/${b.id}`} className="business-card">
                  <div className="business-card-content">
                    <Building2 size={24} className="business-icon" />
                    <div>
                      <h4 className="business-name">{b.name}</h4>
                      <div className="business-meta">
                        <span className="business-type">{b.type}</span>
                        <span className="ownership-badge">{b.roles[user.uid]}</span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="chevron" />
                </Link>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function BusinessCard({ business, showOwnership, onUpdate, onDelete, onDragStart, onDragOver, onDrop, isDragging }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(business.name);

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(`Delete "${business.name}"? This permanently removes all CAPEX, OPEX, claims, and documents. This cannot be undone.`)) {
      onDelete(business.id);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editName.trim() && editName.trim() !== business.name) {
      onUpdate(business.id, { name: editName.trim() });
    }
    setEditing(false);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditName(business.name);
    setEditing(false);
  };

  const startEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditing(true);
  };

  if (editing) {
    return (
      <div className="business-card editing">
        <div className="business-card-content">
          <Building2 size={24} className="business-icon" />
          <div className="biz-edit-form" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave(e);
                if (e.key === 'Escape') handleCancel(e);
              }}
              className="biz-edit-input"
              autoFocus
            />
            <div className="business-meta">
              <span className="business-type">{business.type}</span>
              {showOwnership && business.ownershipPct != null && (
                <span className="ownership-badge">{business.ownershipPct}% owned</span>
              )}
            </div>
          </div>
        </div>
        <div className="biz-edit-actions">
          <button className="btn btn-ghost btn-sm" onClick={handleSave}><Check size={16} /></button>
          <button className="btn btn-ghost btn-sm" onClick={handleCancel}><X size={16} /></button>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/business/${business.id}`}
      className={`business-card ${isDragging ? 'dragging' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="biz-drag-handle" title="Drag to reorder">⋮⋮</div>
      <div className="business-card-content">
        <Building2 size={24} className="business-icon" />
        <div>
          <h4 className="business-name">{business.name}</h4>
          <div className="business-meta">
            <span className="business-type">{business.type}</span>
            {showOwnership && business.ownershipPct != null && (
              <span className="ownership-badge">{business.ownershipPct}% owned</span>
            )}
          </div>
        </div>
      </div>
      <div className="biz-card-actions">
        <button className="btn btn-ghost btn-sm biz-edit-btn" onClick={startEdit} title="Rename">
          <Pencil size={14} />
        </button>
        <button className="btn btn-ghost btn-sm biz-edit-btn" onClick={handleDelete} title="Delete" style={{ color: 'var(--soft-red)' }}>
          <Trash2 size={14} />
        </button>
        <ChevronRight size={20} className="chevron" />
      </div>
    </Link>
  );
}

function AccessManagement({ user, businesses, inviteToBusiness, getPendingInvites, revokeInvite, revokeAccess }) {
  const [showPanel, setShowPanel] = useState(false);
  const [selectedBizIds, setSelectedBizIds] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [allPendingInvites, setAllPendingInvites] = useState([]);

  // Load pending invites for all selected businesses
  useEffect(() => {
    if (!selectedBizIds.length || !getPendingInvites) return;
    Promise.all(selectedBizIds.map((id) => getPendingInvites(id))).then((results) => {
      const all = results.flat();
      setAllPendingInvites(all);
    });
  }, [selectedBizIds, getPendingInvites, success]);

  const toggleBiz = (id) => {
    setSelectedBizIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedBizIds.length === businesses.length) {
      setSelectedBizIds([]);
    } else {
      setSelectedBizIds(businesses.map((b) => b.id));
    }
  };

  const selectedBusinesses = businesses.filter((b) => selectedBizIds.includes(b.id));

  // Filter roles: show OS roles if any selected biz has Internal OS, recon if any has recon
  const anyHasOS = selectedBusinesses.some((b) => b.apps?.internalOS);
  const anyHasRecon = selectedBusinesses.some((b) => b.apps?.recon);

  const availableRoles = ALL_ROLES.filter((r) => {
    if (r.value === 'owner' || r.value === 'viewer') return true;
    if (['ops_staff', 'content_manager', 'biz_dev', 'trainer'].includes(r.value)) return anyHasOS;
    if (r.value === 'accountant') return anyHasRecon;
    return true;
  });

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!selectedBizIds.length || !inviteEmail.trim()) return;
    setSending(true);
    const email = inviteEmail.trim();
    const names = [];
    try {
      for (const bizId of selectedBizIds) {
        await inviteToBusiness(bizId, email, inviteRole);
        const biz = businesses.find((b) => b.id === bizId);
        if (biz) names.push(biz.name);
      }
      setSuccess(`Invited ${email} as ${inviteRole} to ${names.join(', ')}`);
      setInviteEmail('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      alert('Failed: ' + err.message);
    }
    setSending(false);
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm('Cancel this pending invite?')) return;
    await revokeInvite(inviteId);
    setSuccess('revoked-' + Date.now());
  };

  const handleRevokeAccess = async (bizId, uid) => {
    if (uid === user.uid) {
      alert("You can't remove your own access.");
      return;
    }
    const biz = businesses.find((b) => b.id === bizId);
    if (!window.confirm(`Remove access from ${biz?.name || bizId}?`)) return;
    await revokeAccess(bizId, uid);
  };

  // Gather members across all selected businesses
  const membersByUid = {};
  selectedBusinesses.forEach((biz) => {
    Object.entries(biz.roles || {}).forEach(([uid, role]) => {
      if (!membersByUid[uid]) membersByUid[uid] = [];
      membersByUid[uid].push({ bizId: biz.id, bizName: biz.name, role });
    });
  });

  return (
    <div className="access-management">
      <div className="access-header" onClick={() => setShowPanel(!showPanel)}>
        <div className="access-header-left">
          <Users size={18} />
          <span>Access Management</span>
        </div>
        <span className="phase-chevron" style={{ transform: showPanel ? 'rotate(90deg)' : 'none', transition: '0.2s' }}>&#9658;</span>
      </div>

      {showPanel && (
        <div className="access-panel">
          {/* Business selector - multi-select */}
          <div className="access-section">
            <div className="access-label-row">
              <label className="access-label">Select Businesses / Projects</label>
              <button className="btn btn-ghost btn-sm" onClick={selectAll} style={{ fontSize: 11 }}>
                {selectedBizIds.length === businesses.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="access-biz-grid">
              {businesses.map((b) => (
                <button
                  key={b.id}
                  className={`access-biz-btn ${selectedBizIds.includes(b.id) ? 'active' : ''}`}
                  onClick={() => toggleBiz(b.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedBizIds.includes(b.id)}
                    readOnly
                    style={{ accentColor: 'var(--sage)', width: 15, height: 15, pointerEvents: 'none' }}
                  />
                  <Building2 size={14} />
                  <span>{b.name}</span>
                  {b.apps?.internalOS && <Monitor size={11} className="access-app-icon" title="Internal OS" />}
                  {b.apps?.recon && <Calculator size={11} className="access-app-icon" title="Recon" />}
                </button>
              ))}
            </div>
            {selectedBizIds.length > 0 && (
              <p className="access-hint" style={{ marginTop: 8 }}>
                {selectedBizIds.length} selected. Invite will apply to all selected.
              </p>
            )}
          </div>

          {selectedBizIds.length > 0 && (
            <>
              {/* Invite form */}
              <div className="access-section">
                <label className="access-label">Invite Team Member to {selectedBizIds.length === 1 ? selectedBusinesses[0]?.name : `${selectedBizIds.length} businesses`}</label>
                <form className="access-invite-form" onSubmit={handleInvite}>
                  <div className="access-invite-row">
                    <div className="access-email-wrap">
                      <Mail size={16} className="access-email-icon" />
                      <input
                        type="email"
                        placeholder="Email address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        required
                      />
                    </div>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="access-role-select"
                    >
                      {availableRoles.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <button type="submit" className="btn btn-primary" disabled={sending}>
                      <UserPlus size={16} /> {sending ? '...' : 'Invite'}
                    </button>
                  </div>
                </form>
                {success && !success.startsWith('revoked') && (
                  <div className="access-success">{success}</div>
                )}
              </div>

              {/* Current members - grouped by user across businesses */}
              <div className="access-section">
                <label className="access-label">Current Access</label>
                {Object.keys(membersByUid).length === 0 ? (
                  <p className="access-hint">No members yet.</p>
                ) : (
                  <div className="access-members">
                    {Object.entries(membersByUid).map(([uid, entries]) => (
                      <div key={uid} className="access-member-card">
                        <div className="access-member-header">
                          <Shield size={14} className={entries.some((e) => e.role === 'owner') ? 'role-owner' : 'role-viewer'} />
                          <span className="access-member-uid">{uid === user.uid ? 'You' : uid.slice(0, 20) + '...'}</span>
                        </div>
                        <div className="access-member-businesses">
                          {entries.map(({ bizId, bizName, role }) => (
                            <div key={bizId} className="access-member-biz-row">
                              <span className="access-member-biz-name">{bizName}</span>
                              <span className={`member-role-badge ${role}`}>{role}</span>
                              {uid !== user.uid && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => handleRevokeAccess(bizId, uid)}
                                  style={{ color: 'var(--soft-red)', padding: '2px 4px', minHeight: 'auto' }}
                                  title={`Remove from ${bizName}`}
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending invites */}
              {allPendingInvites.length > 0 && (
                <div className="access-section">
                  <label className="access-label">Pending Invites</label>
                  <div className="access-members">
                    {allPendingInvites.map((inv) => {
                      const biz = businesses.find((b) => b.id === inv.businessId);
                      return (
                        <div key={inv.id} className="access-member-row pending">
                          <div className="access-member-info">
                            <Clock size={14} style={{ color: 'var(--warm-gold)' }} />
                            <span>{inv.email}</span>
                            <span className="access-member-biz-name" style={{ fontSize: 11 }}>{biz?.name}</span>
                            <span className={`member-role-badge ${inv.role}`}>{inv.role}</span>
                          </div>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleRevokeInvite(inv.id)}
                            style={{ color: 'var(--soft-red)' }}
                            title="Cancel invite"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
